/**
 * Discovery brief → PDF (client-side via jsPDF, lazy-loaded).
 *
 * The jsPDF library + its dependencies (html2canvas, dompurify) add
 * ~450KB to the bundle. We only need them when the caller clicks
 * Download — which only happens at the end of an intake call — so we
 * import them dynamically. The main bundle stays under ~50KB gzipped
 * for everyone (sales callers especially never load the PDF code).
 */

// Resolve a structural shape for jsPDF without an eager import. Avoids
// pulling the package into the main bundle while still giving us
// type-safety inside the renderer.
type JsPDFInstance = {
  text: (text: string | string[], x: number, y: number, opts?: { align?: string }) => void;
  setFillColor: (color: string) => void;
  setTextColor: (color: string) => void;
  setFont: (font: string, style: string) => void;
  setFontSize: (size: number) => void;
  rect: (x: number, y: number, w: number, h: number, style?: string) => void;
  splitTextToSize: (text: string, width: number) => string[];
  addPage: () => void;
  setPage: (n: number) => void;
  getNumberOfPages: () => number;
  save: (filename: string) => void;
};

// The server-side Gherkin renderer produces a canonical text form;
// we render here as a styled, multi-page PDF. Conservative styling —
// black on white with a single brand-gold accent — so it looks
// professional in any inbox or print queue. Triggered by the
// BriefSubmitted thank-you card's "Download" button.

export interface PdfBriefScenario {
  name: string;
  given: string[];
  when: string[];
  then: string[];
}

export interface PdfBriefPayload {
  reference: string;
  caller: {
    name: string;
    email: string;
    organisation?: string;
    role?: string;
  };
  feature: {
    title: string;
    narrative: string;
  };
  background: string[];
  scenarios: PdfBriefScenario[];
  constraints: string[];
  success_criteria: string[];
  next_step: string;
  receivedAt: string;
}

const GOLD = '#b07a1a';
const INK = '#1a1a1a';
const MUTED = '#666666';
const BG_TINT = '#f7f3ea';

const PAGE_W = 210; // A4 mm
const PAGE_H = 297;
const MARGIN_X = 18;
const MARGIN_TOP = 22;
const MARGIN_BOTTOM = 18;
const LINE_BODY = 5; // mm per body line
const LINE_SECTION_GAP = 4; // mm gap between sections

/**
 * Render the brief into a jsPDF document. Async — jsPDF is loaded on demand.
 */
export async function renderBriefPdf(brief: PdfBriefPayload): Promise<JsPDFInstance> {
  // Dynamic import so jsPDF + html2canvas don't ship in the main bundle.
  const mod = (await import('jspdf')) as {
    default?: new (opts?: object) => JsPDFInstance;
    jsPDF?: new (opts?: object) => JsPDFInstance;
  };
  const JsPDFCtor = mod.default ?? mod.jsPDF;
  if (!JsPDFCtor) throw new Error('jsPDF default export not found');
  const doc = new JsPDFCtor({ unit: 'mm', format: 'a4' });
  const ctx = new RenderCursor(doc);

  drawHeader(ctx, brief);
  drawCallerBlock(ctx, brief);
  drawFeatureBlock(ctx, brief);
  if (brief.background.length > 0) drawBackground(ctx, brief);
  drawScenarios(ctx, brief);
  if (brief.constraints.length > 0) drawList(ctx, 'Constraints', brief.constraints);
  if (brief.success_criteria.length > 0) drawList(ctx, 'Success criteria', brief.success_criteria);
  drawNextStep(ctx, brief);
  drawFooter(doc, brief);

  return doc;
}

/**
 * Trigger a download in the browser. Returns the suggested filename.
 */
export async function downloadBriefPdf(brief: PdfBriefPayload): Promise<string> {
  const doc = await renderBriefPdf(brief);
  const filename = buildFilename(brief);
  doc.save(filename);
  return filename;
}

function buildFilename(brief: PdfBriefPayload): string {
  const slug = brief.feature.title
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 40);
  return `discovery-brief-${slug || brief.reference.toLowerCase()}.pdf`;
}

/**
 * Cursor that tracks vertical position and paginates as needed.
 */
class RenderCursor {
  doc: JsPDFInstance;
  y: number = MARGIN_TOP;

  constructor(doc: JsPDFInstance) {
    this.doc = doc;
  }

  /** Reserve `height` mm of space; advance cursor; paginate if it would overflow. */
  reserve(height: number): void {
    if (this.y + height > PAGE_H - MARGIN_BOTTOM) {
      this.doc.addPage();
      this.y = MARGIN_TOP;
    }
  }

  text(text: string, height = LINE_BODY): void {
    this.reserve(height);
    this.doc.text(text, MARGIN_X, this.y);
    this.y += height;
  }

  gap(mm: number = LINE_SECTION_GAP): void {
    this.y += mm;
  }
}

function drawHeader(ctx: RenderCursor, brief: PdfBriefPayload): void {
  const { doc } = ctx;

  // Brand bar
  doc.setFillColor(GOLD);
  doc.rect(0, 0, PAGE_W, 14, 'F');
  doc.setTextColor('#ffffff');
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  doc.text("THE ARTIFICER'S AI", MARGIN_X, 9);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.text('Discovery Brief', PAGE_W - MARGIN_X, 9, { align: 'right' });

  ctx.y = 22;

  doc.setTextColor(INK);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(18);
  const titleLines = doc.splitTextToSize(brief.feature.title, PAGE_W - MARGIN_X * 2);
  for (const line of titleLines) {
    ctx.text(line, 8);
  }
  ctx.gap(2);

  doc.setTextColor(MUTED);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  ctx.text(`Reference ${brief.reference} · captured ${formatDate(brief.receivedAt)}`);
  ctx.gap(LINE_SECTION_GAP);
}

function drawCallerBlock(ctx: RenderCursor, brief: PdfBriefPayload): void {
  sectionLabel(ctx, 'Captured with');
  bodyText(ctx, brief.caller.name);
  bodyText(ctx, brief.caller.email);
  if (brief.caller.organisation) bodyText(ctx, brief.caller.organisation);
  if (brief.caller.role) bodyText(ctx, brief.caller.role);
  ctx.gap(LINE_SECTION_GAP);
}

function drawFeatureBlock(ctx: RenderCursor, brief: PdfBriefPayload): void {
  sectionLabel(ctx, 'Feature');
  bodyText(ctx, brief.feature.title, true);
  bodyText(ctx, brief.feature.narrative);
  ctx.gap(LINE_SECTION_GAP);
}

function drawBackground(ctx: RenderCursor, brief: PdfBriefPayload): void {
  sectionLabel(ctx, 'Background');
  brief.background.forEach((line, i) => {
    bodyText(ctx, `${i === 0 ? 'Given' : 'And'} ${line}`);
  });
  ctx.gap(LINE_SECTION_GAP);
}

function drawScenarios(ctx: RenderCursor, brief: PdfBriefPayload): void {
  sectionLabel(ctx, 'Scenarios');
  brief.scenarios.forEach((scenario, idx) => {
    if (idx > 0) ctx.gap(2);
    // Scenario header in tinted band
    ctx.reserve(LINE_BODY + 2);
    ctx.doc.setFillColor(BG_TINT);
    ctx.doc.rect(MARGIN_X - 2, ctx.y - LINE_BODY + 1, PAGE_W - MARGIN_X * 2 + 4, LINE_BODY + 1, 'F');
    bodyText(ctx, scenario.name, true);
    scenario.given.forEach((line, i) => {
      bodyText(ctx, `  ${i === 0 ? 'Given' : 'And'} ${line}`);
    });
    scenario.when.forEach((line, i) => {
      bodyText(ctx, `  ${i === 0 ? 'When' : 'And'} ${line}`);
    });
    scenario.then.forEach((line, i) => {
      bodyText(ctx, `  ${i === 0 ? 'Then' : 'And'} ${line}`);
    });
  });
  ctx.gap(LINE_SECTION_GAP);
}

function drawList(ctx: RenderCursor, label: string, items: string[]): void {
  sectionLabel(ctx, label);
  for (const item of items) {
    bodyText(ctx, `• ${item}`);
  }
  ctx.gap(LINE_SECTION_GAP);
}

function drawNextStep(ctx: RenderCursor, brief: PdfBriefPayload): void {
  sectionLabel(ctx, 'Suggested next step');
  bodyText(ctx, brief.next_step);
}

function drawFooter(doc: JsPDFInstance, brief: PdfBriefPayload): void {
  const pages = doc.getNumberOfPages();
  for (let i = 1; i <= pages; i++) {
    doc.setPage(i);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.setTextColor(MUTED);
    doc.text(
      `${brief.caller.name}${brief.caller.organisation ? ' · ' + brief.caller.organisation : ''}`,
      MARGIN_X,
      PAGE_H - 8,
    );
    doc.text(
      `Page ${i} / ${pages}`,
      PAGE_W - MARGIN_X,
      PAGE_H - 8,
      { align: 'right' },
    );
  }
}

function sectionLabel(ctx: RenderCursor, label: string): void {
  const { doc } = ctx;
  doc.setTextColor(GOLD);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8.5);
  ctx.text(label.toUpperCase(), 4.5);
}

function bodyText(ctx: RenderCursor, text: string, bold = false): void {
  const { doc } = ctx;
  doc.setTextColor(INK);
  doc.setFont('helvetica', bold ? 'bold' : 'normal');
  doc.setFontSize(10.5);
  // Word-wrap to page width
  const lines = doc.splitTextToSize(text, PAGE_W - MARGIN_X * 2);
  for (const line of lines) {
    ctx.text(line);
  }
}

function formatDate(iso: string): string {
  try {
    const d = new Date(iso);
    return d.toLocaleString('en-AU', {
      timeZone: 'Australia/Perth',
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    }) + ' AWST';
  } catch {
    return iso;
  }
}
